import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../environment/environment';
import { ReponseApi, TicketBrut } from './EntiteProjet';
import { extraireReponse } from './api';
import { Authentification } from './authentification';

@Injectable({
  providedIn: 'root',
})
export class TicketService {
  private readonly auth = inject(Authentification);
  private readonly http = inject(HttpClient);

  /**
   * Recupere le detail complet d'un ticket via `GET /ticket/:id`.
   * Retourne le ticket brut renvoye par l'API, ou `null` si la reponse
   * ne contient pas de charge utile exploitable.
   */
  async getTicket(ticketId: string): Promise<TicketBrut | null> {
    const url = `${environment.apiUrl}/ticket/${ticketId}`;
    const reponse = await firstValueFrom(
      this.http.get<ReponseApi<TicketBrut>>(url, this.auth.prepareHeaders()),
    );
    return extraireReponse(reponse);
  }

  /**
   * Ajoute un message a un ticket via `POST /ticket/:id/message`.
   * Envoie un payload `{ message }` puis retourne le ticket mis a jour
   * tel que renvoye par l'API.
   */
  async envoyerMessageTicket(ticketId: string, message: string): Promise<TicketBrut | null> {
    const url = `${environment.apiUrl}/ticket/${ticketId}/message`;
    const body = { message };
    const reponse = await firstValueFrom(
      this.http.post<ReponseApi<TicketBrut>>(url, body, this.auth.prepareHeaders()),
    );
    return extraireReponse(reponse);
  }

  /**
   * Met a jour l'etat d'un ticket via `PUT /ticket/:id`.
   * Envoie un payload `{ etat }` correspondant au code d'etat attendu par l'API
   * puis retourne le ticket mis à jour. Les erreurs API sont propagées.
   */
  async updateTicketState(ticketId: string, etat: string): Promise<TicketBrut | null> {
    const url = `${environment.apiUrl}/ticket/${ticketId}`;
    const body = { etat };

    const reponse = await firstValueFrom(
      this.http.put<ReponseApi<TicketBrut>>(url, body, this.auth.prepareHeaders()),
    );
    // Une réponse OK sans ticket reste un succès pour les anciennes versions de l’API.
    return extraireReponse(reponse) ?? {};
  }
}
